import React, { useState } from 'react';
import useDataFetching from '../../hooks/useDataFetching';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import IncidentsList from './IncidentsList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getIncidents = async () => {
  const response = await fetch(`${BACKEND_URL}/api/incidents`);
  if (!response.ok) {
    throw new Error('Failed to fetch incidents');
  }
  return response.json();
};

const AgenticAI = () => {
  const [activeTab, setActiveTab] = useState('incidents');

  // Fetch incidents (no auto-refresh)
  const { data: incidents, loading, error, refetch } = useDataFetching(
    getIncidents,
    []
  );

  if (loading && !incidents) {
    return <LoadingState message="Loading Agentic AI..." fullScreen />;
  }

  if (error && !incidents) {
    return <ErrorState message={error} onRetry={refetch} fullScreen />;
  }

  const tabs = [
    { id: 'incidents', label: 'Incidents' },
    { id: 'workflows', label: 'AI Workflows' }
  ];

  return (
    <div className="min-h-full bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Agentic AI</h1>
            <p className="text-gray-600 mt-1">
              AI agents automatically detecting, analyzing, and resolving incidents
            </p>
          </div>
          <div className="flex gap-3">
            <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
              + New Workflow
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
              Configure Agents
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  pb-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-600 mb-2">Total Incidents</div>
            <div className="text-2xl font-bold text-gray-900">{incidents?.length || 0}</div>
            <div className="mt-2 text-sm text-gray-600">Active</div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-600 mb-2">Auto-Resolved</div>
            <div className="text-2xl font-bold text-green-600">
              {incidents?.filter(i => i.state === 'Resolved').length || 0}
            </div>
            <div className="mt-2 text-sm text-gray-600">By AI Agents</div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-600 mb-2">In Progress</div>
            <div className="text-2xl font-bold text-blue-600">
              {incidents?.filter(i => i.state === 'In Progress').length || 0}
            </div>
            <div className="mt-2 text-sm text-gray-600">Being handled</div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-600 mb-2">Avg Resolution Time</div>
            <div className="text-2xl font-bold text-purple-600">4.2 hrs</div>
            <div className="mt-2 text-sm text-gray-600">AI-powered</div>
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'incidents' && (
          <IncidentsList incidents={incidents || []} />
        )}

        {activeTab === 'workflows' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center py-12">
              <span className="text-5xl mb-4 block">⚙️</span>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Workflows</h3>
              <p className="text-gray-600">Configure automated workflows for your AI agents</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgenticAI;
